import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { hasRole } from "../constants/roles";

const IS_DEMO = import.meta.env.VITE_APP_ENV === "demo";
const UNAUTH_REDIRECT = IS_DEMO ? "/demo" : "/login";

export function ProtectedRoute({
  children,
  roles,
}: {
  children: ReactNode;
  roles?: string[];
}): ReactNode {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to={UNAUTH_REDIRECT} replace />;
  }

  if (roles && !hasRole(user, roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/** Wraps a route that requires both authentication AND a specific feature permission. */
export function FeatureRoute({
  children,
  feature,
}: {
  children: ReactNode;
  feature: string;
}): ReactNode {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!user) return <Navigate to={UNAUTH_REDIRECT} replace />;

  // Super admin always has access; others check the permissions map
  if (
    user.role?.name !== "super_admin" &&
    user.permissions != null &&
    user.permissions[feature] === false
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
